import React from 'react';
import ReactDOM from 'react-dom/client';
import '@fontsource/inter/300.css';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
import './index.css';
import './styles/globals.css';
import './styles/adminPageShell.css';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import App from './App';
import { AuthProvider } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { theme } from './theme';
import { emitSessionExpired, SessionExpiredReason } from './utils/authEvents';
import './index.css';

// Safety net: any query/mutation that fails with 401 (expired/invalid session)
// triggers the global session-expired logout flow. This guarantees the user is
// logged out with a clear message instead of seeing an empty/blank screen, even
// if a particular call site swallows the error. The axios interceptor also
// emits this event; emitSessionExpired is debounced so duplicates are harmless.
function handleApiError(error: unknown): void {
  const err = error as any;
  // Already pre-empted by the request interceptor (locally-expired token).
  if (err?.__sessionExpired) return;
  if (err?.response?.status !== 401) return;

  const code: SessionExpiredReason =
    (err.response?.data?.code as SessionExpiredReason) || 'AUTH_FAILED';
  const message =
    code === 'TOKEN_EXPIRED'
      ? 'Your session has expired. Please log in again.'
      : code === 'USER_INACTIVE'
        ? 'Your account is no longer active. Please log in again.'
        : 'You have been signed out. Please log in again.';

  emitSessionExpired({ reason: code, message });
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: handleApiError }),
  mutationCache: new MutationCache({ onError: handleApiError }),
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      // Treat list data as fresh for 60s so revisiting a page / re-mounting a
      // component reuses cache instead of refetching. Mutations still invalidate
      // their query keys explicitly, so this does not show stale data after edits.
      staleTime: 60_000,
      // Keep unused data in cache for 5 minutes before garbage collection.
      gcTime: 5 * 60_000,
    },
  },
});

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true
      }}
    >
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <AuthProvider>
            <CartProvider>
              <App />
            </CartProvider>
          </AuthProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </BrowserRouter>
  </React.StrictMode>
);
